import React, { useState } from "react";
import { useCountries } from "../data/utils/countries";

const CIVILITIES = [
  { value: "MR", label: "Monsieur" },
  { value: "MRS", label: "Madame" },
  { value: "MISS", label: "Mademoiselle" },
];

const DOCUMENT_TYPES = [
  { value: "PASSPORT", label: "Passeport" },
  { value: "ID", label: "Carte d’identité" },
];

const TYPE_LABELS = {
  ADULT: "Adulte",
  CHILD: "Enfant",
  HELD_INFANT: "Bébé",
};

// Initialise passagers selon travelerPricings
function getInitialPassengers(travelerPricings) {
  const types = travelerPricings?.length
    ? travelerPricings.map((tp) => tp.travelerType)
    : ["ADULT"];
  return types.map((type) => ({
    civility: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    type,
    email: "",
    phone: "",
    countryCode: "225",
    documentNumber: "",
    documentType: "",
    documentExpiry: "",
    issuingCountry: "",
  }));
}

export default function PassengerForm({ flight, onClose, onReservation }) {
  const countries = useCountries();
  const travelerPricings = flight?.travelerPricings || [];
  const [passengers, setPassengers] = useState(
    getInitialPassengers(travelerPricings)
  );
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const itineraryType = flight.itineraries.some((it) =>
    it.segments.some((seg) => seg.arrival.iataCode !== "ABJ")
  )
    ? "international"
    : "domestic";

  const adjustedPrice =
    itineraryType === "international"
      ? parseInt(flight.price.total) + 50000
      : parseInt(flight.price.total);

  const validatePassengers = () => {
    for (const p of passengers) {
      if (
        !p.firstName ||
        !p.lastName ||
        !p.dateOfBirth ||
        !p.documentNumber ||
        !p.documentType ||
        !p.documentExpiry ||
        !p.issuingCountry
      ) {
        alert("⚠️ Merci de compléter toutes les informations obligatoires.");
        return false;
      }
      if (p.type === "ADULT" && (!p.email || !p.phone)) {
        alert("⚠️ L’adulte doit avoir un email et un numéro de téléphone.");
        return false;
      }
      if (itineraryType === "international" && p.documentType === "ID") {
        alert("⚠️ Pour les vols internationaux, seul le Passeport est accepté.");
        return false;
      }
    }
    return true;
  };

  const handleChange = (idx, field, value) => {
    setPassengers((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  const handleNext = () => step < passengers.length - 1 && setStep(step + 1);
  const handlePrev = () => step > 0 && setStep(step - 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validatePassengers()) return;

    const travelers = passengers.map((p, idx) => ({
      id: String(idx + 1),
      travelerType: p.type,
      name: {
        firstName: p.firstName.trim().toUpperCase(),
        lastName: p.lastName.trim().toUpperCase(),
      },
      gender: p.civility === "MR" ? "MALE" : "FEMALE",
      dateOfBirth: p.dateOfBirth,
      documents: [
        {
          documentType: p.documentType,
          number: p.documentNumber,
          expiryDate: p.documentExpiry,
          issuanceCountry: p.issuingCountry,
          nationality: p.issuingCountry,
          holder: true,
        },
      ],
      ...(p.type === "ADULT" && {
        contact: {
          emailAddress: p.email,
          phones: [
            {
              deviceType: "MOBILE",
              countryCallingCode: p.countryCode,
              number: p.phone,
            },
          ],
        },
      }),
    }));

    const flightWithMeta = {
      ...flight,
      adjustedPrice,
      itineraryType,
    };

    const orderPayload = {
      data: {
        type: "flight-order",
        flightOffers: [flightWithMeta],
        travelers,
      },
    };

    setLoading(true);
    fetch(
      "https://djatervoyage.com/backend_djatervoyage/backend_payment/verify_payment.php",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderPayload,
          phone_number: passengers[0].phone,
          email: passengers[0].email,
        }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        if (data.status === "success") {
          alert("✅ Réservation enregistrée avec succès !");
          if (onReservation)
            onReservation({ ...data, email: passengers[0].email });
          if (onClose) onClose();
        } else {
          alert("❌ Erreur réservation : " + data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        alert("⚠️ Impossible d’enregistrer la réservation.");
      });
  };

  const p = passengers[step];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      style={{ backdropFilter: "blur(5px)" }}
    >
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white mt-4 text-lg font-semibold animate-pulse">
            Enregistrement de la réservation...
          </p>
        </div>
      )}

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 text-2xl"
          aria-label="Fermer"
        >
          ✕
        </button>

        <div className="p-6 border-b rounded-t-2xl bg-gradient-to-r from-blue-50 to-blue-100">
          <h2 className="text-xl font-bold flex items-center justify-between">
            <span className="text-blue-700">
              {flight.itineraries[0]?.segments[0]?.departure?.iataCode} →{" "}
              {flight.itineraries[0]?.segments.at(-1)?.arrival?.iataCode}
            </span>
            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
              Étape {step + 1}/{passengers.length}
            </span>
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Renseignez les informations du passager{" "}
            <span className="font-medium text-blue-600">
              ({TYPE_LABELS[p.type]})
            </span>
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-6"
        >
         <div className="border rounded-lg p-4 bg-gray-50 shadow-sm">
            <div className="grid grid-cols-1 gap-4">
              {/* Civilité */}
              <div>
                <label className="block text-sm font-medium mb-1">Civilité</label>
                <select
                  value={p.civility}
                  onChange={(e) =>
                    handleChange(step, "civility", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {CIVILITIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Prénom */}
              <div>
                <label className="block text-sm font-medium mb-1">Prénom</label>
                <input
                  type="text"
                  placeholder="Prénom"
                  value={p.firstName}
                  onChange={(e) =>
                    handleChange(step, "firstName", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              {/* Nom */}
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  type="text"
                  placeholder="Nom"
                  value={p.lastName}
                  onChange={(e) => handleChange(step, "lastName", e.target.value)}
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              {/* Date naissance */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Date de naissance
                </label>
                <input
                  type="date"
                  value={p.dateOfBirth}
                  onChange={(e) =>
                    handleChange(step, "dateOfBirth", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              {/* Infos adulte */}
              {p.type === "ADULT" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      placeholder="exemple@email.com"
                      value={p.email}
                      onChange={(e) =>
                        handleChange(step, "email", e.target.value)
                      }
                      className="border p-2 rounded w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Téléphone
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={p.countryCode}
                        onChange={(e) =>
                          handleChange(step, "countryCode", e.target.value)
                        }
                        className="border p-2 rounded w-1/3"
                        required
                      >
                        {countries.map((c) => (
                          <option key={c.phoneCode} value={c.phoneCode}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        placeholder="Numéro"
                        value={p.phone}
                        onChange={(e) =>
                          handleChange(step, "phone", e.target.value)
                        }
                        className="border p-2 rounded w-2/3"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Document */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Numéro de document
                </label>
                <input
                  type="text"
                  placeholder="Numéro"
                  value={p.documentNumber}
                  onChange={(e) =>
                    handleChange(step, "documentNumber", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Type de document
                </label>
                <select
                  value={p.documentType}
                  onChange={(e) =>
                    handleChange(step, "documentType", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {DOCUMENT_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Date d’expiration
                </label>
                <input
                  type="date"
                  value={p.documentExpiry}
                  onChange={(e) =>
                    handleChange(step, "documentExpiry", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Nationalité (pays émetteur)
                </label>
                <select
                  value={p.issuingCountry}
                  onChange={(e) =>
                    handleChange(step, "issuingCountry", e.target.value)
                  }
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {/* Navigation */}
          <div className="flex justify-between gap-3 sticky bottom-0 bg-white pt-3">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg"
            >
              Annuler
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg font-semibold"
                >
                  Précédent
                </button>
              )}
              {step < passengers.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Confirmer
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
